<template>
    <div ref="navWrapper" class="nav-wrapper" :class="isDesktop ? 'desktop' : 'mobile'">
        <ConditionalOffsetBackground
            :use-offset="useOffset && isDesktop"
            class="nav-wrapper z-50"
            :class="isDesktop ? 'desktop' : 'mobile'"
        >
            <div class="lg:fixed w-full lg:top-0 lg:z-50">
                <UContainer class="pt-3 lg:pt-0 lg:px-7.5">
                    <NotchedBar
                        :flat-top="isDesktop"
                        :margin="0"
                        :padding="0"
                        :height="isDesktop ? navigationHeight : undefined"
                        background-var="#1f2c4a"
                        class="!pb-1 !mb-[0.6rem]"
                        :class="isDesktop ? '!px-1 !-mx-2' : '!pt-3'"
                        :ui="{
                            center: 'justify-center !left-4 !right-4 !top-0 !transform-none',
                        }"
                    >
                        <template #center>
                            <UHeader
                                v-model:open="mobileDrawerOpen"
                                toggle-side="left"
                                :mode="isDesktop ? undefined : 'slideover'"
                                :ui="{
                                    root: 'w-full bg-transparent backdrop-blur-none border-none',
                                    left: 'flex justify-between w-full',
                                    right: 'hidden',
                                    container: '!px-0 gap-0 !max-w-full',
                                    content:
                                        'bg-(--ui-bg-primary) divide-(--ui-border-primary-accented)',
                                    overlay: 'bg-(--ui-scrim)',
                                    toggle: 'lg:flex text-(--ui-text-white) hover:text-(--ui-text-white) active:text-(--ui-text-white) cursor-pointer hover:bg-(--ui-primary-highlighted) active:bg-(--ui-primary)',
                                }"
                                :menu="{
                                    side: 'left',
                                    ui: {
                                        close: 'text-(--ui-text-white) hover:text-(--ui-text-white) active:text-(--ui-text-white) cursor-pointer hover:bg-(--ui-primary-highlighted) active:bg-(--ui-primary)',
                                    },
                                }"
                            >
                                <template #left>
                                    <!-- Put all content here for full control over order of contents -->
                                    <img
                                        :src="craneLogoMinimalWhiteUrl"
                                        alt="CRANE"
                                        class="h-[15px] w-auto object-contain cursor-pointer"
                                        @click="redirectToHomePage"
                                    />

                                    <UNavigationMenu
                                        v-if="isDesktop"
                                        :items="menuLinks"
                                        orientation="horizontal"
                                        variant="link"
                                        :ui="{
                                            list: 'list gap-5 after:block after:border-r-1 after:order-2 after:h-4.5 after:w-px after:border-(--ui-text-white) after:border-1 after:mx-2',
                                            item: 'order-3 nth-1:order-1 nth-2:order-1',
                                            link: '[.list>li>&]:text-lg text-(--ui-text-white) hover:text-(--ui-text-white) active:text-(--ui-text-white) cursor-pointer gap-0 border-b-1 border-transparent data-[state=open]:border-(--ui-text-white)',
                                            linkLabel: 'text-(--ui-text-white)',
                                            linkTrailingIcon: 'hidden',
                                            childList: 'child-list',
                                            // Moves the menu items down to provide more breathing room for the popover and centers the popover
                                            viewportWrapper:
                                                // Change width from a hardcoded value to a dynamic value based on the number of menu items
                                                '!min-w-[calc(100%+3rem)] !top-[calc(100%+1rem)] left-1/2 -translate-x-1/2',
                                            viewport:
                                                'text-(--ui-text-white) bg-(--ui-bg-primary) ring-(--ui-border-primary-accented) shadow-lg',
                                        }"
                                        :unmount-on-hide="false"
                                    >
                                        <template #item-content="{ item }">
                                            <NavigationSubmenu :item="item" />
                                        </template>
                                    </UNavigationMenu>

                                    <div
                                        class="flex -mr-1.5"
                                        :class="isDesktop ? '' : 'flex-row-reverse'"
                                    >
                                        <!-- TODO: add search button back in once we have search working for designer -->
                                        <!-- <UButton
                                            v-if="isDesktop"
                                            icon="i-lucide-search"
                                            size="xl"
                                            variant="ghost"
                                            :ui="{ base: 'cursor-pointer' }"
                                        /> -->
                                        <CustomerMenu
                                            @redirect-to-my-account="redirectToMyAccount"
                                        />
                                        <!-- TODO: add shopping bag button back in once we have shopping bag working for designer -->
                                        <!-- <UButton
                                            icon="i-lucide-shopping-bag"
                                            size="xl"
                                            variant="ghost"
                                            :ui="{ base: 'cursor-pointer' }"
                                        /> -->
                                    </div>
                                </template>

                                <template #content>
                                    <div
                                        class="px-4 sm:px-6 h-(--ui-header-height) shrink-0 flex items-center justify-between gap-3"
                                    >
                                        <UButton
                                            icon="i-lucide-x"
                                            size="xl"
                                            variant="ghost"
                                            :ui="{ base: 'text-(--ui-text-white) cursor-pointer' }"
                                            @click.stop="mobileDrawerOpen = false"
                                        >
                                        </UButton>
                                    </div>
                                    <div class="p-4 sm:p-6 overflow-y-auto">
                                        <UNavigationMenu
                                            :items="menuLinks"
                                            orientation="vertical"
                                            :ui="{
                                                list: 'list',
                                                link: '[.list>li>&]:text-lg text-(--ui-text-white) hover:text-(--ui-text-white) active:text-(--ui-text-white) cursor-pointer hover:before:bg-(--ui-primary-highlighted) active:before:bg-(--ui-primary)',
                                                linkLabel: 'text-(--ui-text-white)',
                                                separator:
                                                    'px-2 h-px bg-(--ui-border-primary-accented)',
                                                childList:
                                                    'ms-5 border-s border-(--ui-border-primary-accented)',
                                            }"
                                        />
                                    </div>
                                </template>
                            </UHeader>
                        </template>
                    </NotchedBar>
                </UContainer>
            </div>
        </ConditionalOffsetBackground>
    </div>
</template>

<script setup lang="ts">
import type { NavigationMenuItem } from '@nuxt/ui'
import NavigationSubmenu from './NavigationSubmenu.vue'
import CustomerMenu from '~~/layers/base/components/adaptive/CustomerMenu.vue'
import ConditionalOffsetBackground from '~~/layers/base/components/shared/ConditionalOffsetBackground.vue'
import NotchedBar from '~~/layers/base/components/shared/NotchedBar.vue'
import { createNavigationLinks } from '~~/apps/web/data/navigationLinks'
import craneLogoMinimalWhiteUrl from '~~/layers/base/assets/Crane_Logo_Minimal_White.svg?url'

// PROPS DEFINITION
interface Props {
    useOffset?: boolean
}

const _props = withDefaults(defineProps<Props>(), {
    useOffset: true,
})

const emit = defineEmits<{
    'redirect-to-my-account': []
    'redirect-to-home-page': []
}>()

// Runtime config for storefront URL
const runtimeConfig = useRuntimeConfig() as {
    public?: {
        storefrontUrl?: string
    }
}
const { isDesktop } = useIsDesktop()

const storefrontUrl = runtimeConfig.public?.storefrontUrl
const navigationHeight = 4.5625
const mobileDrawerOpen = ref(false)
const navigationHeightRem = computed(() => `${navigationHeight}rem`)

// Modified menu links structure based on crane.com Shop menu for desktop
const menuLinks = computed<NavigationMenuItem[]>(() => createNavigationLinks(storefrontUrl || ''))

const redirectToMyAccount = () => {
    emit('redirect-to-my-account')
}

const redirectToHomePage = () => {
    emit('redirect-to-home-page')
}
</script>

<style scoped>
.nav-wrapper.desktop {
    height: v-bind(navigationHeightRem);
}

.nav-wrapper.mobile {
    height: 5.625rem;
}
</style>
